import type { ObservabilityState, RunRecord, StepRecord } from '../store/useObservabilityStore'

export type FlowNode = {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, unknown>
  className?: string
}

export type FlowEdge = {
  id: string
  source: string
  target: string
  animated?: boolean
}

export type FlowGraph = {
  nodes: FlowNode[]
  edges: FlowEdge[]
  layoutKey: number
}

function statusColor(status: string): string {
  switch (status) {
    case 'running':
      return '#2f80ed'
    case 'waiting':
      return '#f2a93b'
    case 'succeeded':
      return '#2f9e44'
    case 'failed':
      return '#d94841'
    default:
      return '#6b7280'
  }
}

function buildRunNode(run: RunRecord): FlowNode {
  const animated = run.status === 'running'
  return {
    id: `run:${run.id}`,
    type: 'runNode',
    position: { x: 0, y: 0 },
    className: animated ? 'status-running animated' : `status-${run.status}`,
    data: {
      kind: 'run',
      id: run.id,
      status: run.status,
      color: statusColor(run.status),
      animated,
      label: 'Run',
    },
  }
}

function buildStepNode(step: StepRecord, index: number): FlowNode {
  const animated = step.status === 'running'
  return {
    id: `step:${step.id}`,
    type: 'stepNode',
    position: {
      x: 220 * index,
      y: 180,
    },
    className: animated ? 'status-running animated' : `status-${step.status}`,
    data: {
      kind: 'step',
      id: step.id,
      stepType: step.stepType,
      stepIndex: step.stepIndex,
      status: step.status,
      color: statusColor(step.status),
      animated,
      label: step.stepType,
    },
  }
}

export function getNodesAndEdges(state: Pick<ObservabilityState, 'run' | 'steps' | 'layoutVersion'>): FlowGraph {
  const nodes: FlowNode[] = []
  const edges: FlowEdge[] = []

  if (!state.run) {
    return { nodes, edges, layoutKey: state.layoutVersion }
  }

  const runNode = buildRunNode(state.run)
  nodes.push(runNode)

  const steps = Object.values(state.steps).sort((a, b) => a.stepIndex - b.stepIndex)
  let previousNodeID = runNode.id
  for (let idx = 0; idx < steps.length; idx += 1) {
    const step = steps[idx]
    const node = buildStepNode(step, idx)
    nodes.push(node)
    edges.push({
      id: `${previousNodeID}->${node.id}`,
      source: previousNodeID,
      target: node.id,
      animated: step.status === 'running',
    })
    previousNodeID = node.id
  }

  return {
    nodes,
    edges,
    layoutKey: state.layoutVersion,
  }
}
